import React, { useState } from 'react';

// Button
export function Btn({ children, variant = 'primary', size = 'md', onClick, type = 'button', disabled, style = {} }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    border: 'none', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit', fontWeight: 500, transition: 'all 0.15s',
    opacity: disabled ? 0.55 : 1,
    padding: size === 'sm' ? '6px 12px' : size === 'lg' ? '12px 24px' : '9px 18px',
    fontSize: size === 'sm' ? 13 : size === 'lg' ? 15 : 14,
  };
  const variants = {
    primary:  { background: 'var(--navy)', color: '#fff' },
    accent:   { background: 'var(--accent)', color: '#1A3C6E' },
    success:  { background: 'var(--success)', color: '#fff' },
    danger:   { background: 'var(--danger)', color: '#fff' },
    ghost:    { background: 'transparent', color: 'var(--heading)', border: '1px solid var(--border)' },
    outline:  { background: 'transparent', color: 'var(--blue)', border: '1px solid var(--blue)' },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      style={{ ...base, ...variants[variant], ...style }}>
      {children}
    </button>
  );
}

// Badge / Status chip
export function Badge({ label, color = '#64748b' }) {
  const bg = color + '18';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
      color, background: bg, border: `1px solid ${color}30`, whiteSpace: 'nowrap'
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
      {label}
    </span>
  );
}

// Card
export function Card({ children, style = {}, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 12,
      padding: '20px 24px', boxShadow: 'var(--shadow)',
      cursor: onClick ? 'pointer' : 'default', ...style
    }}>
      {children}
    </div>
  );
}

// Input
// Eye / eye-off icon for the password show/hide toggle
function EyeIcon({ off }) {
  return off ? (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export function Input({ label, error, style = {}, type, ...props }) {
  const [visible, setVisible] = useState(false);
  const isPassword = type === 'password';
  const inputType = isPassword ? (visible ? 'text' : 'password') : type;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, ...style }}>
      {label && <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)' }}>{label}</label>}
      <div style={{ position: 'relative', display: 'flex' }}>
        <input type={inputType} style={{
          width: '100%', boxSizing: 'border-box',
          padding: '9px 12px', paddingInlineEnd: isPassword ? 40 : 12,
          border: `1px solid ${error ? 'var(--danger)' : 'var(--border)'}`,
          borderRadius: 8, fontSize: 14, outline: 'none', fontFamily: 'inherit',
          background: 'var(--input-bg)', color: 'var(--text)', transition: 'border-color 0.15s'
        }} {...props} />
        {isPassword && (
          <button type="button" onClick={() => setVisible(v => !v)} tabIndex={-1}
            aria-label={visible ? 'Hide password' : 'Show password'}
            title={visible ? 'Hide password' : 'Show password'}
            style={{
              position: 'absolute', insetInlineEnd: 8, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subtle)',
              display: 'flex', alignItems: 'center', padding: 2,
            }}>
            <EyeIcon off={visible} />
          </button>
        )}
      </div>
      {error && <span style={{ fontSize: 12, color: 'var(--danger)' }}>{error}</span>}
    </div>
  );
}

// Select
export function Select({ label, error, style = {}, children, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, ...style }}>
      {label && <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)' }}>{label}</label>}
      <select style={{
        padding: '9px 12px', border: `1px solid ${error ? 'var(--danger)' : 'var(--border)'}`,
        borderRadius: 8, fontSize: 14, outline: 'none', fontFamily: 'inherit',
        background: 'var(--input-bg)', color: 'var(--text)', cursor: 'pointer'
      }} {...props}>
        {children}
      </select>
      {error && <span style={{ fontSize: 12, color: 'var(--danger)' }}>{error}</span>}
    </div>
  );
}

// Textarea
export function Textarea({ label, style = {}, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, ...style }}>
      {label && <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)' }}>{label}</label>}
      <textarea style={{
        padding: '9px 12px', border: '1px solid var(--border)',
        borderRadius: 8, fontSize: 14, outline: 'none', fontFamily: 'inherit',
        background: 'var(--input-bg)', color: 'var(--text)', resize: 'vertical', minHeight: 80
      }} {...props} />
    </div>
  );
}

// Modal
export function Modal({ open, onClose, title, children, width = 560 }) {
  if (!open) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'var(--overlay)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: 'var(--card-bg)', borderRadius: 14, width: '100%', maxWidth: width,
        maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 24px', borderBottom: '1px solid var(--border)'
        }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--heading)' }}>{title}</h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: 20, cursor: 'pointer',
            color: 'var(--text-subtle)', lineHeight: 1, padding: 4
          }}>×</button>
        </div>
        <div style={{ padding: '24px' }}>{children}</div>
      </div>
    </div>
  );
}

// Page header
export function PageHeader({ title, subtitle, actions }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28, gap: 12, flexWrap: 'wrap' }}>
      <div>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--heading)', letterSpacing: '-0.5px' }}>{title}</h1>
        {subtitle && <p style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 4 }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{actions}</div>}
    </div>
  );
}

// Stat card
export function StatCard({ label, value, icon, color = '#1A3C6E', sub }) {
  return (
    <Card style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <div style={{
        width: 48, height: 48, borderRadius: 12, background: color + '14',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0
      }}>{icon}</div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', lineHeight: 1.2 }}>{value}</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{label}</div>
        {sub && <div style={{ fontSize: 12, color, marginTop: 2, fontWeight: 500 }}>{sub}</div>}
      </div>
    </Card>
  );
}

// Empty state
export function Empty({ icon = '📭', message, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 24px', color: 'var(--text-subtle)' }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>{icon}</div>
      <p style={{ fontSize: 15, marginBottom: 16 }}>{message}</p>
      {action}
    </div>
  );
}

// Loading spinner
export function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '48px', color: 'var(--text-subtle)' }}>
      <div style={{
        width: 36, height: 36, border: '3px solid var(--border)',
        borderTopColor: 'var(--navy)', borderRadius: '50%',
        animation: 'spin 0.7s linear infinite'
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// Toast hook
export function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = (message, type = 'success') => {
    const id = Date.now();
    setToasts(t => [...t, { id, message, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3000);
  };
  const ToastContainer = () => (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          padding: '12px 18px', borderRadius: 10, fontSize: 14, fontWeight: 500,
          background: t.type === 'error' ? '#EF4444' : t.type === 'warning' ? '#F59E0B' : '#10B981',
          color: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          animation: 'slideIn 0.2s ease'
        }}>
          {t.message}
        </div>
      ))}
      <style>{`@keyframes slideIn { from { opacity:0; transform: translateY(8px); } to { opacity:1; transform: translateY(0); } }`}</style>
    </div>
  );
  return { show, ToastContainer };
}
